<template>
    <div class="white">
        <div class="header">
            <span class="font-8 appCount">
                {{applicationCount}} Apps found
            </span>
            <span class="right search">
                <input type="text" v-model.trim="searchInput" placeholder="Search" class="small" />
                <IconBase class="icon" :iconColor="iconColor" width="12" height="12" iconName="searchIcon"></IconBase>
            </span>
        </div>
        <Table class="tableSize"
               :loading="loading"
               :columns="columns"
               :rows="tableData"
        />
    </div>
</template>

<script>
import Table from 'components/table/FusionTable.vue';
import IconBase from 'components/IconBase.vue';
import consts from 'services/metaData/consts';
import monetizeAppService from 'services/monetizeApp';
import dashboardConsts from './consts';
import DashboardCellAppInfo from './components/DashboardCellAppInfo/DashboardCellAppInfo.vue';
import DashboardCellArchive from './components/DashboardCellArchive/DashboardCellArchive.vue';
import DashboardCellMediationAdUnits
    from './components/DashboardCellMediationAdUnits/DashboardCellMediationAdUnits.vue';

export default {
    name: 'DashboardTable',
    components: { IconBase, Table },
    props: {
        archiveUpdate: {
            type: Object
        }
    },
    data() {
        return {
            columns: dashboardConsts.columns,
            loading: false,
            rows: [],
            searchInput: '',
            iconColor: consts.COLORS.LIGHT_700
        };
    },
    computed: {
        tableData() {
            if (this.searchInput) {
                return this.rows.filter((row) => row.data.name.toLowerCase()
                    .includes(this.searchInput.toLowerCase()));
            }

            return this.rows;
        },
        applicationCount() {
            return this.tableData.length;
        }
    },
    watch: {
        archiveUpdate() {
            this.getData(true);
        }
    },
    methods: {
        async getData(refresh = false) {
            this.loading = true;
            try {
                const { data: { applications } } = await monetizeAppService
                    .getMonetizeApplications({ refresh, extraFields: ',liveInStore,restrictedUsers,mediationAdUnits' });
                this.formatData(applications);
            } catch (e) {
                this.formatData([]);
            } finally {
                this.loading = false;
            }
        },
        formatData(data) {
            data.sort((a, b) => a.archived - b.archived);

            const formattedData = data.map((row) => {
                const appInfo = this.buildAppInfo(row);
                const mediationAdUnits = this.buildMediationAdUnits(row);
                const archived = this.buildArchived(row);

                return {
                    appInfo,
                    mediationAdUnits,
                    archived,
                    data: row
                };
            });

            this.rows = formattedData;
        },
        buildAppInfo(row) {
            return {
                vueComponent: DashboardCellAppInfo,
                props: {
                    name: row.name,
                    appKey: row.appKey,
                    icon: row.icon,
                    platform: row.platform,
                    archived: row.archived
                },
                events: {
                    editAppClicked: this.editAppClicked
                }
            };
        },
        buildMediationAdUnits(row) {
            return {
                vueComponent: DashboardCellMediationAdUnits,
                props: {
                    adUnitStatus: row.mediationAdUnits,
                    archived: row.archived,
                    appKey: row.appKey
                },
                events: {
                    adUnitClicked: this.adUnitClicked
                }
            };
        },
        buildArchived(row) {
            return {
                vueComponent: DashboardCellArchive,
                props: {
                    archived: row.archived,
                    appKey: row.appKey,
                    name: row.name
                },
                events: {
                    archiveClicked: this.handleArchive
                }
            };
        },
        handleArchive(archiveInfo) {
            this.$emit('handleArchive', archiveInfo);
        },
        editAppClicked(appKey) {
            this.$emit('editAppClicked', { appId: appKey });
        },
        adUnitClicked({ appKey, adUnit }) {
            this.$emit('adUnitClicked', { app: appKey, adUnit });
        }
    },
    created() {
        this.getData();
    }
};
</script>

<style scoped lang="scss">
    @import "~styles/styleGuide";
    @import "~styles/input";

    /deep/ {
        --table-header-bottom-border-color: #{$primary-500};
        --table-odd-row-background-color: #{$white};
        --table-even-row-background-color: #{$light-100};
        --table-header-font-family: #{Open Sans};
        --table-header-font-size: #{12px};
        --table-header-font-weight: #{600};
        --table-body-border: #{$border};
        --table-empty-image: none;

        .dashboardCellArchive {
            @import "./components/DashboardCellArchive/DashboardCellArchive";
        }

        .dashboardCellMediationAdUnits {
            @import "./components/DashboardCellMediationAdUnits/DashboardCellMediationAdUnits";
        }

        .dashboardCellAppInfo {
            @import "./components/DashboardCellAppInfo/DashboardCellAppInfo";
        }

        .is-nodata {
            label {
                color: $light-700;
                font-size: 22px;
            }
        }
    }

    .header {
        line-height: 48px;

        .appCount {
            color: $light-700;
        }

        .search {
            position: relative;

            .icon {
                position: absolute;
                margin-left: -20px;
            }
        }
    }
    .white {
        background: white;
    }

    .tableSize {
        $disclaimerSize: 2 * 20px;
        $noLayoutPad: 10px;

        @media #{$smallLaptop} {
            height: #{516px - $disclaimerSize + $noLayoutPad};
        }

        @media #{$largeLaptop} {
            height: #{616px  - $disclaimerSize + $noLayoutPad};
        }

        @media #{$desktop} {
            height: #{726px - $disclaimerSize + $noLayoutPad};
        }
    }
</style>
